import React, { useEffect, useState } from "react";
import { Box, Flex, HStack, Text, Icon, Button, IconButton, Tooltip } from "@chakra-ui/react";
import { HiOutlineArrowLeft, HiOutlinePlus, HiOutlineArrowsPointingIn, HiOutlineArrowsPointingOut } from "react-icons/hi2";
import { c, glassSurface } from "../classroomColors";
import { STANDE, brand } from "../standeTokens";

/**
 * Back to the session, what is being taught, and the way in to content —
 * floating along the top of the canvas (wireframe 265:30).
 *
 * The bar itself lets the canvas through; only its three pieces take the
 * pointer, so the strip between them is still somewhere to draw.
 */
/**
 * Ending the class for everyone takes two presses: the first asks, the
 * second does it. A stray click in front of a class must not end it.
 */
const EndSessionButton = ({ onEnd }) => {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!asking) return undefined;
    const t = setTimeout(() => setAsking(false), 4000);
    return () => clearTimeout(t);
  }, [asking]);
  return (
    <Button
      onClick={async () => {
        if (!asking) return setAsking(true);
        setBusy(true);
        await onEnd();
        setBusy(false);
      }}
      isLoading={busy}
      size="sm"
      h="40px"
      px="16px"
      borderRadius="200px"
      {...(asking ? { bg: c.solidBg, color: c.solidText, _hover: { bg: c.solidBg, opacity: 0.86 } } : { ...glassSurface, color: c.text, _hover: { bg: c.hover } })}
      fontSize="13.5px"
      fontWeight={asking ? "600" : "400"}
      pointerEvents="auto"
      flexShrink={0}
    >
      {asking ? "End for everyone?" : "End session"}
    </Button>
  );
};

/** Start the session for everyone: the one blue thing in the room's bar. */
const GoLiveButton = ({ onGoLive }) => {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      onClick={async () => {
        setBusy(true);
        await onGoLive();
        setBusy(false);
      }}
      isLoading={busy}
      size="sm"
      h="40px"
      px="18px"
      borderRadius="200px"
      bg={STANDE.primaryBlue}
      color={STANDE.black}
      fontSize="13.5px"
      fontWeight="600"
      _hover={{ bg: STANDE.primaryBlue, filter: "brightness(0.94)" }}
      _active={{ bg: STANDE.primaryBlue, filter: "brightness(0.9)" }}
      pointerEvents="auto"
      flexShrink={0}
    >
      Go live
    </Button>
  );
};

/** Full screen on and off. Esc also leaves it; the icon follows either way. */
const FullscreenButton = ({ fullscreen }) => (
  <Tooltip label={fullscreen.isFullscreen ? "Exit full screen (Esc)" : "Full screen"} hasArrow openDelay={300}>
    <IconButton
      aria-label={fullscreen.isFullscreen ? "Exit full screen" : "Full screen"}
      icon={<Icon as={fullscreen.isFullscreen ? HiOutlineArrowsPointingIn : HiOutlineArrowsPointingOut} boxSize="17px" />}
      onClick={fullscreen.toggle}
      size="sm"
      h="40px"
      w="40px"
      minW="40px"
      borderRadius="200px"
      {...glassSurface}
      color={c.text}
      _hover={{ bg: c.hover }}
      pointerEvents="auto"
      flexShrink={0}
    />
  </Tooltip>
);

const InClassHeader = ({ classroomName, topic, isDark, onBack, onOpenContent, onEndSession, onGoLive, readOnly, opening, fullscreen }) => (
  <Flex
    position="absolute"
    top="0"
    left="0"
    right="0"
    zIndex={20}
    align="flex-start"
    justify="space-between"
    gap="16px"
    px="20px"
    pt="18px"
    pointerEvents="none"
  >
    <Flex
      as="button"
      type="button"
      align="center"
      gap="8px"
      h="40px"
      pl="6px"
      pr="16px"
      borderRadius="200px"
      {...glassSurface}
      onClick={onBack}
      color={c.textSub}
      _hover={{ color: c.text }}
      transition="color 0.15s"
      pointerEvents="auto"
      flexShrink={0}
    >
      <Flex boxSize="28px" borderRadius="full" align="center" justify="center" bg={c.surfaceSubtle}>
        <Icon as={HiOutlineArrowLeft} boxSize="14px" />
      </Flex>
      <Text fontSize="14px" fontWeight="400">
        Back
      </Text>
    </Flex>

    {/* Centred on the window, not on the room the two sides leave. */}
    <HStack
      position="absolute"
      left="50%"
      top="18px"
      transform="translateX(-50%)"
      maxW={{ base: "45vw", md: "420px" }}
      spacing="10px"
      align="center"
      h="48px"
      pl="6px"
      pr="18px"
      borderRadius="14px"
      {...glassSurface}
      pointerEvents="auto"
    >
      <Box boxSize="36px" borderRadius="10px" bg={brand(isDark).lightGreen} flexShrink={0} />
      <Box minW={0}>
        <Text fontSize="14.5px" fontWeight="500" color={c.text} lineHeight="1.2" noOfLines={1}>
          {classroomName}
        </Text>
        <Text fontSize="12px" fontWeight="300" color={c.textSub} noOfLines={1}>
          {topic}
        </Text>
      </Box>
    </HStack>

    <Flex gap="8px" pointerEvents="none" flexShrink={0}>
      {fullscreen?.supported && <FullscreenButton fullscreen={fullscreen} />}
      {!readOnly && onGoLive && <GoLiveButton onGoLive={onGoLive} />}
      {!readOnly && onEndSession && <EndSessionButton onEnd={onEndSession} />}
      {!readOnly && (
      <Button
        rightIcon={<Icon as={HiOutlinePlus} boxSize="15px" />}
        onClick={onOpenContent}
        isDisabled={Boolean(opening)}
        size="sm"
        h="40px"
        px="16px"
        borderRadius="200px"
        {...glassSurface}
        color={c.text}
        fontSize="13.5px"
        fontWeight="400"
        _hover={{ bg: c.hover }}
        pointerEvents="auto"
        flexShrink={0}
      >
        {/* While pages go up, how far along — a 20-page PDF takes a moment. */}
        {opening ? (opening.total ? `Opening ${opening.done}/${opening.total}` : "Opening…") : "Open content"}
      </Button>
      )}
    </Flex>
  </Flex>
);

export default InClassHeader;
